import { useState, useEffect, useMemo } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getConsultants, forcePasswordChange } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';

const EMPTY_FORM = {
  email: '',
  password: '',
  nom: '',
  prenom: '',
  role: 'visiteur',
  consultant_id: '',
  actif: true,
};

export function useUsersManagement() {
  const { showToast } = useToast();

  // --- Données ---
  const [users, setUsers] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Filtres ---
  const [filterRole, setFilterRole] = useState('all');
  const [filterActif, setFilterActif] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // --- Modal création/édition ---
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // --- Modal suppression ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // --- Modal photo ---
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [userForPhoto, setUserForPhoto] = useState(null);

  // --- Chargement initial ---
  useEffect(() => {
    loadUsers();
    loadConsultants();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadConsultants = async () => {
    try {
      const data = await getConsultants();
      setConsultants(data);
    } catch (err) {
      console.error('Erreur chargement consultants:', err);
    }
  };

  // --- Filtrage mémoïsé ---
  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return users.filter(user => {
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesActif = filterActif === 'all' ||
        (filterActif === 'actif' && user.actif) ||
        (filterActif === 'inactif' && !user.actif);
      const matchesSearch = searchTerm === '' ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.prenom?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesRole && matchesActif && matchesSearch;
    });
  }, [users, filterRole, filterActif, searchTerm]);

  // --- Handlers modal création/édition ---
  const handleOpenModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    setFormData(mode === 'edit' && user
      ? { email: user.email, password: '', nom: user.nom, prenom: user.prenom, role: user.role, consultant_id: user.consultant_id || '', actif: user.actif }
      : EMPTY_FORM
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (formData.consultant_id) {
        const linked = users.find(
          u => u.consultant_id === formData.consultant_id &&
               (modalMode === 'create' || u.id !== selectedUser.id)
        );
        if (linked) {
          const consultant = consultants.find(c => c.id === formData.consultant_id);
          const consultantName = consultant?.fields?.Nom || 'ce consultant';
          showToast(`${consultantName} est déjà associé à ${linked.prenom} ${linked.nom}`, 'error');
          return;
        }
      }

      if (modalMode === 'create') {
        await createUser(formData);
        showToast('Utilisateur créé avec succès', 'success');
      } else {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await updateUser(selectedUser.id, updateData);
        showToast('Utilisateur modifié avec succès', 'success');
      }

      await loadUsers();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Handlers suppression ---
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await deleteUser(userToDelete.id);
      showToast('Utilisateur supprimé avec succès', 'success');
      await loadUsers();
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
      setShowDeleteModal(false);
      setUserToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // --- Handlers photo ---
  const handleOpenPhotoModal = (user) => {
    setUserForPhoto(user);
    setShowPhotoModal(true);
  };

  const handlePhotoUpdate = async (photoData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/.netlify/functions/updateUserPhoto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ userId: userForPhoto.id, photoData }),
      });
      const data = await response.json();
      if (response.ok) {
        await loadUsers();
        showToast(data.message, 'success');
      } else {
        showToast(data.error || 'Erreur lors de la mise à jour de la photo', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur lors de la mise à jour de la photo', 'error');
    }
  };

  // --- Handler force changement MDP ---
  const handleForcePasswordChange = async (userId, currentValue) => {
    try {
      const newValue = !currentValue;
      await forcePasswordChange(userId, newValue);
      showToast(
        newValue
          ? "L'utilisateur devra changer son mot de passe à la prochaine connexion"
          : "L'obligation de changement de mot de passe a été retirée",
        'success'
      );
      await loadUsers();
    } catch (err) {
      console.error('Erreur:', err);
      showToast(err.message || 'Erreur lors de la mise à jour', 'error');
    }
  };

  return {
    // Données
    users, consultants, loading, error, filteredUsers,
    // Filtres
    filterRole, setFilterRole,
    filterActif, setFilterActif,
    searchTerm, setSearchTerm,
    // Modal création/édition
    showModal, modalMode, selectedUser, submitting, formData, setFormData,
    handleOpenModal, handleCloseModal, handleSubmit,
    // Modal suppression
    showDeleteModal, userToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    // Modal photo
    showPhotoModal, userForPhoto,
    handleOpenPhotoModal, handlePhotoUpdate,
    setShowPhotoModal, setUserForPhoto,
    // Force MDP
    handleForcePasswordChange,
  };
}
